import { FormEvent, useEffect, useState } from 'react';
import { CloneEnterpriseCalendarPayload } from '../../services/enterpriseCalendarsApi';
import { ModalPortalShell } from '../ui/ModalPortalShell';

type NewCalendarModalProps = {
  open: boolean;
  sourceCalendarId?: string;
  sourceCalendarName?: string;
  onClose: () => void;
  onSubmit: (payload: CloneEnterpriseCalendarPayload) => Promise<void>;
};

export function NewCalendarModal({
  open,
  sourceCalendarId,
  sourceCalendarName,
  onClose,
  onSubmit,
}: NewCalendarModalProps) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [copyEvents, setCopyEvents] = useState(true);
  const [copyWorkSchedule, setCopyWorkSchedule] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName('');
    setCode('');
    setCopyEvents(true);
    setCopyWorkSchedule(true);
  }, [open]);

  if (!open) return null;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!name.trim()) return;

    setSaving(true);
    try {
      await onSubmit({
        name: name.trim(),
        code: code.trim() || undefined,
        sourceCalendarId,
        copyEvents,
        copyWorkSchedule,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortalShell
      open={open}
      panelClassName="max-w-lg rounded-2xl bg-white shadow-xl"
      overlayClassName="z-50"
    >
      <form onSubmit={(event) => void handleSubmit(event)} className="flex min-h-0 flex-1 flex-col">
        <div className="shrink-0 border-b border-slate-100 px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-900">Novo calendário</h2>
          <p className="mt-1 text-sm text-slate-500">
            Clonar de: {sourceCalendarName ?? 'Calendário global'}
          </p>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-6 py-4">
          <label className="block text-sm">
            <span className="mb-1 block text-slate-600">Nome</span>
            <input
              className="w-full rounded-xl border border-slate-300 px-3 py-2"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: Comercial"
              required
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block text-slate-600">Código (opcional)</span>
            <input
              className="w-full rounded-xl border border-slate-300 px-3 py-2"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Ex.: comercial"
            />
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={copyEvents}
              onChange={(e) => setCopyEvents(e.target.checked)}
            />
            Herdar eventos e feriados
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={copyWorkSchedule}
              onChange={(e) => setCopyWorkSchedule(e.target.checked)}
            />
            Herdar horários da semana
          </label>
        </div>

        <div className="erp-btn-actions shrink-0 border-t border-slate-100 px-6 py-4">
          <button type="button" className="erp-btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="erp-btn-primary">
            {saving ? 'Criando...' : 'Criar calendário'}
          </button>
        </div>
      </form>
    </ModalPortalShell>
  );
}
